import { observer } from 'mobx-react-lite';
import EditDraftButton from './EditDraftButton';
import { draftMessageNotSentText, infoScheduledSendOn } from './ItemHeader.locstring.json';
import RecipientWell from './RecipientWell';
import RecipientWellV2 from './RecipientWellV2';
import SentReceivedSavedTime from './SentReceivedSavedTime';
import {
    shouldShowMeetingSummary,
    shouldShowMeetingCardFeedView,
} from '../utils/shouldShowMeetingSummary';
import { AttachmentWellView } from 'owa-attachment-well-view';
import { MailboxType } from 'owa-client-ids';
import { isFeatureEnabled } from 'owa-feature-flags';
import loc, { format } from 'owa-localize';
import { formatWeekDayShortUserDate, formatUserTime, userDate } from 'owa-datetime';
import { sending } from 'owa-locstrings/lib/strings/sending.locstring.json';
import { ViewProfileButton } from 'owa-mail-linkedin-profile';
import { MessageApprovalHeader } from 'owa-mail-message-approval';
import { ItemHeaderInfoBar } from 'owa-mail-reading-pane-infobar-view';
import { ItemActions, ItemActionsMenu } from 'owa-mail-reading-pane-item-actions-view';
import ItemViewState from 'owa-mail-reading-pane-store/lib/store/schema/ItemViewState';
import isLinkedInViewProfileEnabled from 'owa-mail-reading-pane-store/lib/utils/isLinkedInViewProfileEnabled';
import { SenderImageWrapper, SenderPersonaWrapper } from 'owa-mail-sender-persona-view';
import { InstrumentationContext } from 'owa-mail-store';
import { ClientItem, ClientMessage } from 'owa-mail-store';
import { isMeetingMessage, MeetingMessageInfo, MeetingMessageSummary } from 'owa-meeting-message';
import MeetingMessage from 'owa-service/lib/contract/MeetingMessage';
import SharingMessage from 'owa-service/lib/contract/SharingMessage';
import { SharingAcceptButton, shouldShowSharingMessageHeader } from 'owa-sharing-accept';
import doesMessageHaveSmimePrefix from 'owa-smime/lib/utils/doesMessageHaveSmimePrefix';
import * as React from 'react';

const styles = require('./ItemHeader.scss');
const classNames = require('classnames');

const SmimeItemAttachmentActionKeys: string[] = ['Reply', 'ReplyAll', 'Forward', 'Print'];
// Only support S/MIME related infobars for ItemAttachment
const INFOBAR_IDS_ALLOWED_FOR_ITEM_ATTACHMENT = [
    'safetyBar',
    'infoSMIME',
    'infoSmimeDecodeError',
    'infoSmimeSigned',
];

export interface ItemHeaderProps {
    item: ClientItem;
    viewState: ItemViewState;
    shouldIncludeFullInfo: boolean;
    instrumentationContext: InstrumentationContext;
    onExpandCollapse?: (event: React.MouseEvent<HTMLElement>) => void;
    isNodePending?: boolean;
    onLazyMount?: () => void;
    isItemAttachment?: boolean;
    isDeeplink?: boolean;
    isReadonly?: boolean;
}

export default observer(function ItemHeader(props: ItemHeaderProps) {
    const renderMessageApprovalHeader = (message: ClientMessage): JSX.Element => {
        return (
            message?.ApprovalRequestData?.IsUndecidedApprovalRequest && (
                <MessageApprovalHeader item={props.item} />
            )
        );
    };
    const renderInfoBars = (message: ClientMessage, viewState: ItemViewState): JSX.Element => {
        return (
            <ItemHeaderInfoBar
                message={message}
                viewState={{
                    ...viewState,
                    infoBarIds: props.isItemAttachment
                        ? INFOBAR_IDS_ALLOWED_FOR_ITEM_ATTACHMENT
                        : viewState.infoBarIds,
                }}
                instrumentationContext={props.instrumentationContext}
                className={styles.infoBarMargin}
                isItemAttachment={props.isItemAttachment}
            />
        );
    };
    const renderSurfaceButtons = (treatAsDraft: boolean, isNodePending: boolean): JSX.Element => {
        if (isNodePending) {
            // Surface buttons has style of 40px height and float right,
            // render an invisable div to preserve the 40px height
            return <div className={styles.localLieItemActionPlaceholder} />;
        } else if (treatAsDraft) {
            return (
                <EditDraftButton
                    className={styles.draftButtonContainer}
                    itemId={props.item.ItemId}
                    instrumentationContext={props.instrumentationContext}
                    isDeferredSend={!!props.item.DeferredSendTime}
                />
            );
        } else {
            const customizedSubsetActionKeys = doesMessageHaveSmimePrefix(props.item)
                ? SmimeItemAttachmentActionKeys
                : [];
            if (isFeatureEnabled('rp-menuShort')) {
                return (
                    <ItemActionsMenu
                        viewState={props.viewState}
                        item={props.item}
                        shouldIncludeFullInfo={props.shouldIncludeFullInfo}
                        instrumentationContext={props.instrumentationContext}
                        isItemAttachment={props.isItemAttachment}
                        isDeeplink={props.isDeeplink}
                        customizedSubsetActionKeys={customizedSubsetActionKeys}
                    />
                );
            }
            return (
                <ItemActions
                    viewState={props.viewState}
                    item={props.item}
                    shouldIncludeFullInfo={props.shouldIncludeFullInfo}
                    instrumentationContext={props.instrumentationContext}
                    isItemAttachment={props.isItemAttachment}
                    isDeeplink={props.isDeeplink}
                    customizedSubsetActionKeys={customizedSubsetActionKeys}
                />
            );
        }
    };
    const renderTime = (treatAsDraft: boolean, isNodePending: boolean): JSX.Element => {
        if (isNodePending) {
            if (props.item.localLieSentTime) {
                return (
                    <SentReceivedSavedTime
                        className={styles.sentReceivedTimestamp}
                        time={props.item.localLieSentTime}
                    />
                );
            }
            return <div className={styles.sentReceivedTimestamp}> {loc(sending)} </div>;
        } else if (treatAsDraft) {
            if (props.item.DeferredSendTime) {
                const deferredSendAsDate = userDate(props.item.DeferredSendTime);
                return (
                    <div className={styles.draftStatusContainer}>
                        <span className={styles.draftNotSentTextInfo}>
                            {format(
                                loc(infoScheduledSendOn),
                                formatWeekDayShortUserDate(deferredSendAsDate),
                                formatUserTime(deferredSendAsDate)
                            )}
                        </span>
                    </div>
                );
            }
            return (
                <div className={styles.draftStatusContainer}>
                    <span className={styles.draftNotSentTextInfo}>
                        {loc(draftMessageNotSentText)}
                    </span>
                </div>
            );
        } else {
            return (
                <SentReceivedSavedTime
                    className={styles.sentReceivedTimestamp}
                    time={props.item.DateTimeReceived}
                />
            );
        }
    };
    const { item, viewState, isNodePending, isItemAttachment, isDeeplink, isReadonly } = props;
    const mailboxInfo = item.MailboxInfo;
    const message: ClientMessage = item as ClientMessage; // TODO: handle non-message case later.
    const isDraft = item.IsDraft && !isItemAttachment;
    // Hide the surface buttons if
    // - This item is draft item queued for submission (state Delay or Sending)
    // - This item is draft item submitted, but waiting for delivery and not a scheduled send.
    // - This is a readonly reading pane.
    const hideSurfaceButtons =
        item.isDraftQueuedForSubmission ||
        (isDraft && item.IsSubmitted && !item.DeferredSendTime) ||
        isReadonly;
    // treatAsDraft means the item is a draft, but we want to render the UI as if
    // it is not a draft - this is used for local lie and sending in the drafts
    // folder (undo send in particular)
    const treatAsDraft = isDraft && !isNodePending && !item.isDraftQueuedForSubmission;
    const displaySelf = isDraft || isNodePending;
    return (
        <div>
            <div onClick={props.onExpandCollapse}>
                {!isNodePending && renderInfoBars(message, viewState)}
                {isMeetingMessage(item) && shouldShowMeetingSummary(item, isDeeplink) && (
                    <div className={styles.meetingSummaryContainer}>
                        <MeetingMessageSummary
                            item={item as MeetingMessage}
                            entrySource={'MailReadingPane'}
                        />
                    </div>
                )}
                <SenderImageWrapper
                    message={message}
                    displaySelf={displaySelf}
                    style={styles.senderImage}
                    isUnauthenticatedSender={message.AntispamUnauthenticatedSender}
                    showPresence={true}
                    displayPersonaHighlightRing={true}
                />
                <div
                    className={classNames(styles.senderTimeActionsContainer, 'allowTextSelection')}>
                    <div className={styles.senderTimeContainer}>
                        <SenderPersonaWrapper
                            message={message}
                            treatAsDraft={treatAsDraft}
                            isNodePending={isNodePending}
                            displaySelf={displaySelf}
                        />
                        {isLinkedInViewProfileEnabled() && (
                            <ViewProfileButton
                                undoDarkMode={viewState.undoDarkMode}
                                isUnauthenticatedSender={message.AntispamUnauthenticatedSender}
                                sender={message.From ? message.From : message.Sender}
                            />
                        )}
                        {renderTime(treatAsDraft, isNodePending)}
                    </div>
                    <div className={styles.rightContainer}>
                        {!hideSurfaceButtons && renderSurfaceButtons(treatAsDraft, isNodePending)}
                        {treatAsDraft && (
                            <SentReceivedSavedTime
                                className={styles.draftSavedTimestamp}
                                time={props.item.LastModifiedTime}
                                treatAsDraft={true}
                            />
                        )}
                    </div>
                </div>
                {!treatAsDraft && isFeatureEnabled('rp-separateRecipientWells') ? (
                    <RecipientWellV2
                        toRecipients={message.ToRecipients}
                        ccRecipients={message.CcRecipients}
                        bccRecipients={message.BccRecipients}
                        itemId={message.ItemId?.Id}
                        recipientCounts={message.RecipientCounts}
                    />
                ) : (
                    <RecipientWell
                        toRecipients={message.ToRecipients}
                        ccRecipients={message.CcRecipients}
                        bccRecipients={message.BccRecipients}
                        itemId={message.ItemId?.Id}
                        recipientCounts={message.RecipientCounts}
                    />
                )}
            </div>
            <div className={styles.subHeaderContainer}>
                {!isReadonly && (
                    <>
                        {renderMessageApprovalHeader(message)}
                        {isMeetingMessage(item) &&
                            !shouldShowMeetingCardFeedView(item, isDeeplink) && (
                                <MeetingMessageInfo
                                    item={item as MeetingMessage}
                                    groupId={
                                        mailboxInfo.type === MailboxType.GroupMailbox
                                            ? mailboxInfo.mailboxSmtpAddress
                                            : null
                                    }
                                    viewState={viewState.meetingRequestViewState}
                                    onDidMount={props.onLazyMount}
                                    entrySource={'MailReadingPane'}
                                />
                            )}
                        {shouldShowSharingMessageHeader(item) && (
                            <div className={styles.sharingAcceptButton}>
                                <SharingAcceptButton item={item as SharingMessage} />
                            </div>
                        )}
                    </>
                )}
                {viewState.attachmentWell && (
                    <AttachmentWellView
                        attachmentWellViewState={viewState.attachmentWell}
                        parentItemId={{
                            ...item.ItemId,
                            mailboxInfo: item.MailboxInfo,
                        }}
                        isDraft={isDraft}
                        instrumentationContext={props.instrumentationContext}
                    />
                )}
            </div>
        </div>
    );
});
